import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Description as NotesIcon,
  Folder as DocumentsIcon,
  Science as ScienceIcon,
  Edit as EditIcon,
  Storage as StorageIcon,
  RecordVoiceOver as CounselIcon,
} from '@mui/icons-material';

// Import the existing note components from Patient360New
import RxTimelineTab from '../../../../components/patient-360/RxTimelineTab';
import CounselingHistoryTab from '../../../../components/patient-360/CounselingHistoryTab';
import { ComprehensiveNotesPanel } from '../../../../components/patient-360/core/ComprehensiveNotesPanel';
import { PatientNotesPanel } from '../../../../components/patient-360/core/PatientNotesPanel';
import { KrollNotesPanel } from '../../../../components/patient-360/core/KrollNotesPanel';
import { PatientDocumentsPanel } from '../../../../components/patient-360/core/PatientDocumentsPanel';
import { usePharmacyStore } from '../../../../stores/pharmacyStore';

interface NotesAndDocumentsProps {
  patientId: string;
  patientName?: string;
}

export const NotesAndDocuments: React.FC<NotesAndDocumentsProps> = ({ patientId, patientName }) => {
  const theme = useTheme();
  const [subTab, setSubTab] = useState(0);
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  // Bumped when Kroll notes are imported so the sibling note panels remount
  // and re-fetch — same pattern as ClinicalVerificationDialog's krollImportKey
  // (GP-931). Without it an import on this tab can leave All Notes / Manage
  // Notes showing a pre-import snapshot.
  const [krollImportKey, setKrollImportKey] = useState(0);

  // Reset sub-tab when patient changes
  useEffect(() => { setSubTab(0); }, [patientId]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Redundant "Notes & Documents" heading removed (GP-1168) — it just
          restated the already-active top-level tab, wasting vertical space. */}

      {/* Sub-tabs - GREEN background per spec */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.success.main, 0.1), mb: 3, mt: 1, flexShrink: 0 }}>
        <Tabs
          value={subTab}
          onChange={(e, v) => setSubTab(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            }
          }}
        >
          <Tab
            icon={<ScienceIcon fontSize="small" />}
            label="RX History"
            iconPosition="start"
          />
          <Tab
            icon={<DocumentsIcon fontSize="small" />}
            label="Documents"
            iconPosition="start"
          />
          <Tab
            icon={<NotesIcon fontSize="small" />}
            label="All Notes"
            iconPosition="start"
          />
          <Tab
            icon={<EditIcon fontSize="small" />}
            label="Manage Notes"
            iconPosition="start"
          />
          <Tab
            icon={<StorageIcon fontSize="small" />}
            label="Kroll Notes"
            iconPosition="start"
          />
          <Tab
            icon={<CounselIcon fontSize="small" />}
            label="Counseling History"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Sub-tab Content */}
      {/* Sub-tab 1: Clinical History */}
      {subTab === 0 && (
        <Box sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
          <RxTimelineTab
            patientId={patientId}
            pharmacyId={activePharmacy?.id || ''}
            patientName={patientName}
          />
        </Box>
      )}

      {/* Sub-tab 2: Documents — Kroll-side (live) + PHOX uploads (GP-370) */}
      {subTab === 1 && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', width: '100%', display: 'flex' }}>
          <PatientDocumentsPanel patientId={patientId} />
        </Box>
      )}

      {/* Sub-tab 3: All Notes (Read-Only Aggregated View) */}
      {subTab === 2 && (
        <Box sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
          <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotesIcon />
              All Patient Notes (Aggregated View)
            </Typography>
            <ComprehensiveNotesPanel key={`all-notes-${krollImportKey}`} patientId={patientId} />
          </Paper>
        </Box>
      )}

      {/* Sub-tab 4: Manage Notes (Full-Featured Add/Edit) */}
      {subTab === 3 && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', width: '100%', display: 'flex' }}>
          <PatientNotesPanel key={`manage-notes-${krollImportKey}`} patientId={patientId} />
        </Box>
      )}

      {/* Sub-tab 5: Kroll Notes (Read-Only from Kroll System) */}
      {subTab === 4 && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', width: '100%', display: 'flex' }}>
          <KrollNotesPanel
            patientId={patientId}
            onImported={() => setKrollImportKey((k) => k + 1)}
          />
        </Box>
      )}

      {/* Sub-tab 6: Counseling History */}
      {subTab === 5 && (
        <Box sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
          <CounselingHistoryTab
            patientId={patientId}
            pharmacyId={activePharmacy?.id || ''}
            patientName={patientName}
          />
        </Box>
      )}
    </Box>
  );
};
